import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE } from "@/data/site";

/**
 * Per-route <head> management, without pulling in react-helmet.
 *
 * Before this, every one of the 184 public URLs shipped the same title, the same
 * description, no canonical and an og:url pointing at the homepage. Only /webinar
 * overrode anything.
 *
 * Two audiences read what this writes:
 *   - Crawlers that execute JavaScript (Googlebot, Bingbot, Gemini) see it at runtime.
 *   - Crawlers that do not (GPTBot, ClaudeBot, PerplexityBot, OAI-SearchBot, CCBot)
 *     see it only because scripts/prerender.mjs runs a real browser at build time and
 *     saves the resulting HTML per route. That is why every tag here is written into
 *     the live DOM rather than kept in React state: the prerender pass serialises the
 *     DOM, so whatever is not in it does not exist for those crawlers.
 */

type JsonLd = Record<string, unknown> | Record<string, unknown>[];

export type SeoProps = {
  title?: string;
  description?: string;
  /** Absolute path, e.g. "/packs/the-stack-you-sell". Defaults to the current route. */
  path?: string;
  image?: string;
  /** "website" for hubs, "article" for a single lesson, pack or post. */
  type?: "website" | "article";
  /** Keeps a page out of the index without blocking the crawl (login, checkout returns). */
  noindex?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  /** One or more JSON-LD nodes for this route, injected as a single script tag. */
  jsonLd?: JsonLd;
};

const DEFAULT_TITLE = "AI Automation Base — Learn to Build the Systems. Then Own Them.";
const DEFAULT_DESCRIPTION =
  "An online school run by Hector Diaz: live follow-along classes where you install n8n, wire Postgres, connect Claude Code and own the system you build. Mistakes included. From $20 a month.";
const DEFAULT_IMAGE = `${SITE.origin}/og/home.png`;

/** Marks the tags this component owns so it can clean up its own mess between routes. */
const OWNED = "data-seo";

const setMeta = (attr: "name" | "property", key: string, value: string | undefined) => {
  const selector = `meta[${attr}="${key}"]`;
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  if (value === undefined) {
    if (existing?.hasAttribute(OWNED)) existing.remove();
    return;
  }
  const el = existing ?? document.createElement("meta");
  if (!existing) {
    el.setAttribute(attr, key);
    el.setAttribute(OWNED, "");
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};

const setLink = (rel: string, href: string | undefined) => {
  const existing = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (href === undefined) {
    if (existing?.hasAttribute(OWNED)) existing.remove();
    return;
  }
  const el = existing ?? document.createElement("link");
  if (!existing) {
    el.setAttribute("rel", rel);
    el.setAttribute(OWNED, "");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/**
 * Route-level JSON-LD lives in its own tag, tagged so it can be swapped on navigation.
 * The site-wide Organization/Person/WebSite graph stays hard-coded in index.html and is
 * deliberately never touched here — it has to survive with JavaScript switched off.
 */
const setJsonLd = (data: JsonLd | undefined) => {
  const id = "seo-jsonld";
  document.getElementById(id)?.remove();
  if (!data) return;
  const script = document.createElement("script");
  script.id = id;
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};

export const Seo = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path,
  image = DEFAULT_IMAGE,
  type = "website",
  noindex = false,
  publishedTime,
  modifiedTime,
  author,
  jsonLd,
}: SeoProps) => {
  const location = useLocation();
  const resolvedPath = path ?? location.pathname;
  const canonical = `${SITE.origin}${resolvedPath === "/" ? "/" : resolvedPath.replace(/\/+$/, "")}`;
  // Long titles get cut in the SERP anyway; the brand suffix is what gets dropped.
  const fullTitle = title ? `${title} — ${SITE.name}` : DEFAULT_TITLE;

  useEffect(() => {
    document.title = fullTitle;
    setMeta("name", "description", description);
    setLink("canonical", canonical);
    setMeta("name", "robots", noindex ? "noindex, follow" : undefined);

    setMeta("property", "og:title", title ?? DEFAULT_TITLE);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:type", type);
    setMeta("property", "og:image", image);

    setMeta("name", "twitter:title", title ?? DEFAULT_TITLE);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);

    setMeta("property", "article:published_time", type === "article" ? publishedTime : undefined);
    setMeta("property", "article:modified_time", type === "article" ? modifiedTime : undefined);
    setMeta("property", "article:author", type === "article" ? author : undefined);

    setJsonLd(jsonLd);

    // scripts/prerender.mjs waits for this before serialising the DOM. Without it the
    // snapshot can be taken while the route is still on its loading branch, which bakes
    // a spinner and the default title into the file a crawler will read.
    document.documentElement.setAttribute("data-seo-ready", "1");
  }, [fullTitle, title, description, canonical, image, type, noindex, publishedTime, modifiedTime, author, jsonLd]);

  return null;
};

/** Breadcrumbs for a detail page. Google still reads these; models use them for context. */
export const breadcrumbLd = (trail: { name: string; path: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: trail.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: `${SITE.origin}${item.path}`,
  })),
});

export default Seo;
